import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Users, Eye, Rocket, Network, TrendingUp, ChevronRight, Award, Building2, MessageCircle } from 'lucide-react';
import api from '../../lib/api';
import { PartnerChatbot } from '../../components/chatbot/PartnerChatbot';

export const BecomePartnerPage = () => {
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, eventsRes] = await Promise.all([
          api.get('/settings/public'),
          api.get('/events')
        ]);
        setSettings(settingsRes.data);
        // Get only published events for sponsors display
        const publishedEvents = eventsRes.data.filter(e => e.status === 'published');
        setEvents(publishedEvents);
      } catch (error) {
        console.log('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Collect all unique sponsors from all events
  const allSponsors = events.reduce((acc, event) => {
    if (event.sponsors) {
      event.sponsors.forEach(sponsor => {
        if (sponsor.logo && !acc.find(s => s.name === sponsor.name)) {
          acc.push(sponsor);
        }
      });
    }
    return acc;
  }, []);

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Aumenta la tua Brand Awareness',
      description: 'Posiziona la tua azienda al centro dell\'ecosistema dell\'innovazione italiano e internazionale insieme agli attori più importanti'
    },
    {
      icon: Rocket,
      title: 'Promuovi la tua Realtà',
      description: 'Se hai nuovi progetti, prodotti o servizi innovativi, avrai la possibilità di farli conoscere a un ampio pubblico di esperti di innovazione e dei settori emergenti'
    },
    {
      icon: Network,
      title: 'Espandi il tuo Network',
      description: 'Conosci e fatti conoscere. SIOS è un\'occasione per allargare e fortificare relazioni, per incontrare nuovi contatti e porre le basi per future collaborazioni'
    }
  ];

  const stats = [
    { value: '+140', label: 'Speaker' },
    { value: '+100', label: 'Partner' },
    { value: '+2.5ML', label: 'Visualizzazioni' },
    { value: '+2000', label: 'Startup e PMI' }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="become-partner-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-violet-900/20" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-4">
              Vivi l'Innovazione
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Diventa partner del più grande evento sull'innovazione in Italia
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Unisciti a noi e fai parte di un network che include le realtà più innovative dell'ecosistema italiano
            </p>
            <a 
              href="#partner-form" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-colors"
              data-testid="cta-become-partner"
            >
              <Handshake size={20} />
              Richiedi Informazioni
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="glass rounded-2xl p-6 text-center"
              >
                <p className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 bg-black/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">
              Perché collaborare con noi
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Diventa protagonista dell'innovazione
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="glass rounded-2xl p-8 hover:bg-white/5 transition-colors"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
                  <benefit.icon className="text-primary" size={28} />
                </div>
                <h3 className="font-heading text-xl font-bold mb-4">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* Partnership Options */}
          <div className="mt-16 glass rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Award className="text-primary" size={24} />
              </div>
              <h3 className="font-heading text-2xl font-bold">Le nostre offerte di visibilità</h3>
            </div>
            
            <p className="text-lg text-muted-foreground mb-8">
              StartupItalia Open Summit è il più importante evento italiano dedicato al mondo dell'innovazione. 
              Dal 2015 riunisce i principali attori dell'ecosistema: startupper, investitori, incubatori e imprenditori 
              per discutere dei trend più innovativi e di tecnologie all'avanguardia.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="text-primary" size={24} />
                  <h4 className="font-heading text-lg font-bold">Smart Partner</h4>
                </div>
                <p className="text-muted-foreground text-sm">
                  Videointervista, social plus, banner in newsletter, visibilità del tuo logo e citazioni nei comunicati stampa
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="text-primary" size={24} />
                  <h4 className="font-heading text-lg font-bold">Spazio Espositivo</h4>
                </div>
                <p className="text-muted-foreground text-sm">
                  Desk personalizzato per presentare i tuoi prodotti e servizi direttamente ai partecipanti dell'evento
                </p>
              </div>
            </div>

            <div className="text-center mt-8">
              <a 
                href="#partner-form" 
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                Richiedi informazioni sui pacchetti
                <ChevronRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Showcase */}
      {allSponsors.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">
                Chi crede in noi
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">I nostri Partner</h2>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {allSponsors.slice(0, 18).map((sponsor, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-center p-4 rounded-xl border border-gray-200 transition-all hover:shadow-lg"
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <img 
                    src={getFullUrl(sponsor.logo)}
                    alt={sponsor.name}
                    className="max-h-12 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Form Section */}
      <section id="partner-form" className="py-20 bg-black/20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">
              Contattaci
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Scopri come diventare Partner
            </h2>
            <p className="text-muted-foreground">
              {settings?.partner_contact_mode === 'form' 
                ? 'Compila il form per ricevere informazioni sui pacchetti di sponsorizzazione'
                : 'Usa il nostro assistente virtuale per ricevere informazioni sui pacchetti di sponsorizzazione'
              }
            </p>
          </div>

          {/* Form Mode */}
          {settings?.partner_contact_mode === 'form' && settings?.partner_form_html ? (
            <div className="glass rounded-2xl p-8 md:p-12">
              <div 
                dangerouslySetInnerHTML={{ __html: settings.partner_form_html }}
                className="partner-form-embed"
              />
            </div>
          ) : (
            /* Chatbot Mode (default) */
            <div className="glass rounded-2xl p-8 md:p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                <MessageCircle size={40} className="text-primary" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-4">Assistente Virtuale</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Clicca sul pulsante in basso a destra per parlare con il nostro assistente AI. 
                Ti aiuterà a scoprire il pacchetto di partnership più adatto alle tue esigenze.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-primary">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Online 24/7
              </div>
            </div>
          )}

          {/* Contact Info */}
          {(settings?.contact_email || settings?.contact_phone) && (
            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-2">Oppure contattaci direttamente:</p>
              <div className="flex flex-wrap justify-center gap-6">
                {settings?.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="text-primary hover:underline">
                    {settings.contact_email}
                  </a>
                )}
                {settings?.contact_phone && (
                  <a href={`tel:${settings.contact_phone}`} className="text-primary hover:underline">
                    {settings.contact_phone}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Partner Chatbot Widget - only show if explicitly in chatbot mode */}
      {settings && settings.partner_contact_mode === 'chatbot' && <PartnerChatbot />}
    </div>
  );
};

export default BecomePartnerPage;
